import { useMemo } from 'react'
import _toNumber from 'lodash/toNumber'
import { parse as parseQuery } from '../../../services/query-string/index'
import { Location } from '../history-types'
import { OraclePersonalityListQuery, GetChatSessionQuery } from '../queries.generated'
import { PERSONALITY_SAGE_ID } from '../personalityFilter'

interface UseOraclePersonalityParams {
  location: Location
  currentChatSessionId?: string
  chatSessionData?: GetChatSessionQuery
  personalityData?: OraclePersonalityListQuery
  defaultOraclePersonalityId: string
  /**
   * Whether user's primary language is English.
   * Non-English users always get Sage for new chats.
   */
  isEnglishUser?: boolean
}

interface UseOraclePersonalityReturn {
  activePersonalityId: number
  selectedPersonality: OraclePersonality | null
  showRegLink: boolean
  currentPersonalityId: string
}

type OraclePersonality = NonNullable<
  OraclePersonalityListQuery['oraclePersonalityList']
>['oraclePersonalities'][number]

/**
 * Manages Oracle personality selection logic with campaign defaults and URL parameters.
 *
 * Priority order:
 * 1. For existing chat sessions: Use personality from session data
 * 2. For new chats: Use URL parameter
 * 3. Fallback: Use campaign-based default personality
 */
export function useOraclePersonality ({
  location,
  currentChatSessionId,
  chatSessionData,
  personalityData,
  defaultOraclePersonalityId,
  isEnglishUser,
}: UseOraclePersonalityParams): UseOraclePersonalityReturn {
  // Get current personality from URL with campaign-based default fallback
  const currentQuery = parseQuery(location.search)

  // Use URL parameter if present, otherwise use campaign-based default
  const currentPersonalityId = (currentQuery.oraclePersonalityId as string)
                               || defaultOraclePersonalityId

  // Get active personality ID - use session data for existing chats, URL for new chats
  const activePersonalityId: number = useMemo(() => {
    // If we have an existing chat session, use its personality ID (grandfathering)
    if (currentChatSessionId && chatSessionData?.oracleChatSession?.personalityId) {
      return _toNumber(chatSessionData.oracleChatSession.personalityId)
    }

    // Non-English users always get Sage for new chats
    if (isEnglishUser === false) {
      return PERSONALITY_SAGE_ID
    }

    // For new chats (or while data is loading), use URL personality ID
    // which defaults to campaign-based personality. Unrecognized IDs pass
    // through for testing (AI-663).
    return _toNumber(currentPersonalityId)
  }, [
    currentChatSessionId,
    chatSessionData,
    currentPersonalityId,
    defaultOraclePersonalityId,
    isEnglishUser,
  ])

  // Get selected personality from active personality ID
  const selectedPersonality = useMemo(() => {
    if (!personalityData?.oraclePersonalityList?.oraclePersonalities) {
      return null
    }

    // Get personality from active personality ID (either from session or URL)
    return personalityData.oraclePersonalityList.oraclePersonalities.find(
      (p) => p.id === activePersonalityId.toString(),
    ) || null
  }, [personalityData, activePersonalityId])

  const showRegLink = useMemo(
    () => !!(selectedPersonality && selectedPersonality.key.toLowerCase() === 'architect'),
    [selectedPersonality],
  )

  return {
    activePersonalityId,
    selectedPersonality,
    showRegLink,
    currentPersonalityId,
  }
}
